<template>
	<div :class="wrapperClassList">
		<form
			:class="[formClassList]"
			enctype="multipart/form-data"
			@submit.prevent
		>
			<div
				v-tooltip="{content: 'Upload Resume', delay: { show: props.collapsed ? 500 : 999999, hide: 100 }}"
				:class="['flex items-center justify-center', {'won-button-upload rounded': !props.isFlat}]"
			>
				<label
					:class="[
						'flex size-full cursor-pointer items-center justify-center',
						{'px-2.5 py-2': !props.isFlat}
					]"
					@click="selectFileHandler"
				>
					<slot>
						<svg
							v-if="showIcon"
							width="17"
							height="16"
							viewBox="0 0 17 16"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clip-path="url(#clip0_853_10644)">
								<path
									d="M9.83333 1.33331H4.5C3.76666 1.33331 3.17333 1.93331 3.17333 2.66665L3.16666 13.3333C3.16666 14.0666 3.76 14.6666 4.49333 14.6666H12.5C13.2333 14.6666 13.8333 14.0666 13.8333 13.3333V5.33331L9.83333 1.33331ZM12.5 13.3333H4.5V2.66665H9.16666V5.99998H12.5V13.3333ZM5.83333 10.0066L6.77333 10.9466L7.83333 9.89331V12.6666H9.16666V9.89331L10.2267 10.9533L11.1667 10.0066L8.50666 7.33331L5.83333 10.0066Z"
									fill="#2A8176"
								/>
							</g>
							<defs>
								<clipPath id="clip0_853_10644"><rect
									width="16"
									height="16"
									fill="white"
									transform="translate(0.5)"
								/>
								</clipPath>
							</defs>
						</svg>
						<span
							v-if="!props.collapsed"
							:class="[
								'font-[Inter] text-sm font-bold whitespace-nowrap',
								textClassList
							]"
						>
							{{ text }}
						</span>
					</slot>

					<input
						id="upload-resume-input"
						:key="uploadCount"
						type="file"
						accept=".pdf,.docx,.txt"
						class="hidden"
						@change="handleUploadStart"
					/>
				</label>
			</div>
		</form>

		<Teleport to="body">
			<FileHandlerPopover
				v-if="showUploadingPopover"
				class="fixed right-0 bottom-0 z-50"
				:progress="fileUploadingProgress"
				:error="fileUploadingErrorMessage"
				:filename="fileName"
				:uploading="fileUploading"
				@cancel="resetUpload"
			/>
		</Teleport>
        
		<ResumeUploadResult
			:show-modal="showUploadResult"
			:close-modal="hideResumeUploadResult"
			:success="successfullyUploaded"
			:keep-original-format="keepOriginalFormat"
			@view-resume="viewResumeHandler"
		/>
	</div>
</template>

<script setup>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { uploadResume } from '../api/upload';
import { pollResumeStatus } from '../composables/usePollResumeStatus';
import { FileError, FileSizeError } from '../errors';
import { useResumesStore } from '../store/resumes.store';
import { useAuthPopupStore } from '../store/auth-popup.store';
import { useOnboardingChecklist } from '../store/onboarding-checklist.store';

import { setAuthState } from '../composables/auth.js';
import { update } from '../api/profile';
import { useNotifications } from '../store/notifications.store.js';
import { useFeatureUsageStore } from '../store/feature-usage.store';
import { bytesToMegaBytes } from '../utils/helpers';

import ResumeUploadResult from '../modals/upload-resume/upload-result.vue';
import FileHandlerPopover from './file-handler-popover.vue';

const emit = defineEmits(['done', 'view-uploaded-resume']);
const props = defineProps({
	isFlat: {
		type: Boolean,
		default: false,
	},
	showIcon: {
		type: Boolean,
		default: true
	},
	formClassList: {
		type: String,
		default: ''
	},
	text: {
		type: String,
		default: 'Upload Resume'
	},
	wrapperClassList: {
		type: String,
		default: 'w-full mt-2 inline-block'
	},
	textClassList: {
		type: String,
		default: ''
	},
	collapsed: {
		type: Boolean
	}
});

const router = useRouter();
const route = useRoute();
const resumeStore = useResumesStore();
const onboardingChecklist = useOnboardingChecklist();
const authPopupStore = useAuthPopupStore();

const showUploadResult = ref(false);
const { getUser, setUser, isAuthenticated } = setAuthState();

// upload features
const showUploadingPopover = ref(false);
const fileUploading = ref(true);
const fileUploadingProgress = ref(0);
const uploadCount = ref(1);
const fileName = ref('');
const fileUploadingErrorMessage = ref('');
const resumeId = ref(null);
const keepOriginalFormat = ref(false);
const successfullyUploaded = ref(false);
let file = null;

// user
const user = getUser();
const isUploadResumePassed = ref(
	user?.options?.upload_resume_passed || false
);

const notifications = useNotifications();

const selectFileHandler = (event) => {
	if (!isAuthenticated()) {
		event.preventDefault();
		authPopupStore.setShowAuthPopup();
	}
};

const resetUpload = () => {
	fileUploading.value = false;
	showUploadingPopover.value = false;
	fileName.value = '';
	fileUploadingProgress.value = 0;
	fileUploadingErrorMessage.value = '';
	resumeId.value = null;
	keepOriginalFormat.value = false;
	file = null;
};

const featureUsageStore = useFeatureUsageStore();

const notifyUserFirstTime = async () => {
	notifications.notify('You can now upload a resume with up to 15,000 characters.', 'success', 7000);

	const response = await update({
		options: {
			...getUser().options,
			upload_resume_passed: true
		}
	});

	setUser(response.data.data);
};

const hideResumeUploadResult = () => {
	showUploadResult.value = false;
};

const handleUploadStart = async (e) => {
	resetUpload();
	file = e.target.files[0];

	await onUploadResume();

	if (!isUploadResumePassed.value) {
		await notifyUserFirstTime();
	}
};

const viewResumeHandler = () => {
	hideResumeUploadResult();

	if (keepOriginalFormat.value) {
		router.push({ name: 'resumes' });
	} else {
		emit('view-uploaded-resume');
  
		setTimeout(() => {
			router.push({
				name: 'resumes.edit',
				params: { id: resumeId.value },
				query: { from: 'upload' }
			});
		}, 300);
	}
};

const onUploadResume = async ( uploadInOriginalFormat ) => {
	try {
		keepOriginalFormat.value = uploadInOriginalFormat;
		uploadCount.value++;
		showUploadingPopover.value = true;
		fileUploading.value = true;

		// if not selected file reset upload
		if (! file) {
			resetUpload();
			return;
		}

		fileName.value = file.name;

		if (bytesToMegaBytes(file.size) > 10) {
			throw new FileSizeError('Only files up to 10MB are allowed. Please upload a valid resume.', 400);
		}

		const response = await uploadResume(file, uploadInOriginalFormat, {
			onUploadProgress: function(progressEvent) {
				fileUploadingProgress.value = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
			}
		});

		const { data: { data: uploadedResume } } = response;

		const resume = response.status === 202
			? await pollResumeStatus(uploadedResume.uuid)
			: uploadedResume;

		resumeId.value = resume.uuid;
    
		if (resume) {
			onboardingChecklist.markItemAsCompleted(1, 'uploadResume');
		}

		successfullyUploaded.value = true;
		resumeStore.addResumeToStore(resume);

		setTimeout(() => {
			fileUploading.value = false;

			if (route.name !== 'cover-letters.edit') {
				viewResumeHandler();
			}
      
			emit('done', resume);
		}, 300);
    
		featureUsageStore.refresh();

	} catch (err) {
		if (err.status === 429)  {
			showUploadingPopover.value = false;
			featureUsageStore.setShowLimitExceed('resume_upload');
        
			return;
		} else if (err.status === 400) {
			if ( err.message === 'Max characters number exceeded' || 
				err.message === 'OpenAI max token exceeds') {
				fileUploadingErrorMessage.value = 'Your resume has exceeded the limit of 15,000 characters. Please upload a new file.';
			} else if (err.message === 'File must be a PDF, DOCX or TXT') {
				fileUploadingErrorMessage.value = 'Your resume has a wrong format. Please upload a PDF, DOCX, or TXT file.';
			} else {
				fileUploadingErrorMessage.value = err instanceof FileError ? err.message : 'There was an error uploading your resume, please try with another file.';
			}
		} else {
			// EN-1003: includes the poller's 408, where the parse is still running and
			// "try with another file" would send the user to re-upload for nothing.
			fileUploadingErrorMessage.value = err instanceof FileError ? err.message : 'There was an error uploading your resume, please try with another file.';
		}

		notifications.notify(fileUploadingErrorMessage.value, 'error', 7000);
	}
};
</script>
